import React, { useContext, useState } from 'react';
import { AppContext } from '../App';
import { addMockExpense, EXPENSE_TYPES, MOCK_JOBS } from '../utils/mockData';
import { addExpense } from '../utils/api';

const GAS_RATE = 0.67;

export default function AddExpense({ prefill = {} }) {
  const { demoMode, showToast, navigate } = useContext(AppContext);
  const [type, setType] = useState(prefill.type || 'material');
  const [amount, setAmount] = useState(prefill.amount ? String(prefill.amount) : '');
  const [description, setDescription] = useState(prefill.description || '');
  const [jobName, setJobName] = useState(prefill.jobName || '');
  const [customJob, setCustomJob] = useState('');
  const [vendor, setVendor] = useState(prefill.vendor || '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [miles, setMiles] = useState('');
  const [rate, setRate] = useState(String(GAS_RATE));
  const [hours, setHours] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedType = EXPENSE_TYPES.find(t => t.id === type);
  const effectiveJob = jobName === '_custom' ? customJob : jobName;

  const handleMilesChange = (v) => { setMiles(v); if (v && rate) setAmount((parseFloat(v) * parseFloat(rate)).toFixed(2)); };
  const handleRateChange = (v) => { setRate(v); if (miles && v) setAmount((parseFloat(miles) * parseFloat(v)).toFixed(2)); };
  const handleHoursChange = (v) => { setHours(v); if (v && hourlyRate) setAmount((parseFloat(v) * parseFloat(hourlyRate)).toFixed(2)); };
  const handleHourlyRateChange = (v) => { setHourlyRate(v); if (hours && v) setAmount((parseFloat(hours) * parseFloat(v)).toFixed(2)); };

  const handleSave = async () => {
    if (!amount || !effectiveJob || !date) { showToast('Please fill in amount, job, and date', 'error'); return; }
    const expense = {
      type, amount: parseFloat(amount), description: description || (type === 'gas' ? `${miles} miles` : selectedType?.label),
      jobName: effectiveJob, vendor, date, notes,
      ...(type === 'gas' ? { miles: parseFloat(miles), ratePerMile: parseFloat(rate) } : {}),
      ...(type === 'labor' ? { hours: parseFloat(hours), hourlyRate: parseFloat(hourlyRate) } : {})
    };
    setLoading(true);
    try {
      if (demoMode) { addMockExpense(expense); showToast('Expense saved!'); }
      else { await addExpense(expense); showToast('Expense saved!'); }
      navigate('dashboard');
    } catch (err) { showToast('Failed to save expense', 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fade-in">
      <div className="screen-header" style={{ paddingBottom: 16 }}>
        <div><div className="screen-title">ADD EXPENSE</div><div className="screen-subtitle">Log a new job cost</div></div>
      </div>
      <div style={{ padding: '0 16px' }}>
        <div className="form-group">
          <label className="form-label">Expense Type</label>
          <div className="type-selector">
            {EXPENSE_TYPES.map(t => (
              <button key={t.id} className={`type-btn ${type === t.id ? 'active' : ''}`} style={{ '--accent-color': t.color, '--accent-rgb': hexToRgb(t.color) }} onClick={() => setType(t.id)}>
                <span>{t.icon}</span><span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {type === 'gas' && (
          <div className="card" style={{ margin: '0 0 16px', background: 'rgba(230,194,58,0.07)', borderColor: 'rgba(230,194,58,0.25)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-yellow)', letterSpacing: 0.5, marginBottom: 12 }}>⛽ Mileage Calculator</div>
            <div className="mileage-row">
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Miles Driven</label><input className="form-input" type="number" placeholder="0" inputMode="decimal" value={miles} onChange={e => handleMilesChange(e.target.value)} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">$/Mile</label><input className="form-input" type="number" inputMode="decimal" value={rate} onChange={e => handleRateChange(e.target.value)} /></div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>IRS rate: $0.67/mile (2024)</div>
          </div>
        )}

        {type === 'labor' && (
          <div className="card" style={{ margin: '0 0 16px', background: 'rgba(58,155,230,0.07)', borderColor: 'rgba(58,155,230,0.25)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-blue)', letterSpacing: 0.5, marginBottom: 12 }}>👷 Labor Calculator</div>
            <div className="mileage-row">
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Hours</label><input className="form-input" type="number" placeholder="0" inputMode="decimal" value={hours} onChange={e => handleHoursChange(e.target.value)} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">$/Hour</label><input className="form-input" type="number" placeholder="0.00" inputMode="decimal" value={hourlyRate} onChange={e => handleHourlyRateChange(e.target.value)} /></div>
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Amount ($)</label>
          <input className="form-input" type="number" placeholder="0.00" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: 24, color: selectedType?.color || 'var(--text-primary)' }} />
        </div>

        <div className="form-group">
          <label className="form-label">Job / Project</label>
          <select className="form-input" value={jobName} onChange={e => setJobName(e.target.value)}>
            <option value="">Select a job...</option>
            {MOCK_JOBS.map(j => <option key={j} value={j}>{j}</option>)}
            <option value="_custom">+ New Job</option>
          </select>
        </div>

        {jobName === '_custom' && (
          <div className="form-group"><label className="form-label">New Job Name</label><input className="form-input" type="text" placeholder="e.g. Smith Residence - Kitchen" value={customJob} onChange={e => setCustomJob(e.target.value)} /></div>
        )}

        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-input" type="text" placeholder={type === 'material' ? 'e.g. Lumber, drywall, screws' : type === 'labor' ? 'e.g. Tile work - 8 hrs' : 'e.g. Round trip to supplier'} value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        <div className="form-group"><label className="form-label">Vendor / Supplier (optional)</label><input className="form-input" type="text" placeholder="e.g. Home Depot, Lowe's" value={vendor} onChange={e => setVendor(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Date</label><input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Notes (optional)</label><textarea className="form-input" placeholder="Any additional details..." value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={{ resize: 'none', lineHeight: 1.5 }} /></div>

        <button className="btn btn--primary" onClick={handleSave} disabled={loading} style={{ marginBottom: 8 }}>
          {loading ? <span className="spinner" style={{ width: 20, height: 20, borderTopColor: 'white', borderColor: 'rgba(255,255,255,0.3)' }} /> : `Save ${selectedType?.label || 'Expense'}`}
        </button>
        <button className="btn btn--secondary" onClick={() => navigate('dashboard')}>Cancel</button>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}
